<template>
  <expandable-box-node small>
    <template #title>
      <div class="d-flex flex-row gap-2 font-size-14 font-weight-medium">
        <div v-if="selectedQuantity > 0" class="d-flex flex-row align-center gap-1">
          <v-icon dense color="#406611"> mdi-check-circle </v-icon>
          <span style="color: #406611">
            {{ selectedQuantity }}
          </span>
        </div>
        <span>
          {{ title }}
        </span>
      </div>
    </template>

    <div class="d-flex flex-column divide-y">
      <previous-treatment-medicine
        v-for="(medicine, title) in options"
        :key="medicine"
        v-model="modelValue[medicine]"
        :title="title"
        :medicine="medicine"
        :treatmentOptions="treatmentsOptions[medicine]"
      />
      <previous-treatment-medicine
        v-if="hasOther"
        key="other"
        v-model="modelValue['other']"
        title="Outro"
        medicine="other"
      >
        <template #checkbox="{ selected, updateSelected, label, updateLabel }">
          <div class="d-flex flex-row align-center w-100 pr-4">
            <v-simple-checkbox
              color="primary"
              :value="selected"
              class="align-self-start"
              style="margin-top: 10px"
              @input="
                (e) => {
                  updateSelected(e)
                  updateLabel(' ')
                  $nextTick(() => {
                    updateLabel('')
                  })
                }
              "
            />
            <v-text-field
              :value="label"
              @input="
                (e) => {
                  updateLabel(e)
                  if (e?.length && !selected) {
                    updateSelected(true)
                  } else if (!e?.length && selected) {
                    updateSelected(false)
                  }
                }
              "
              filled
              dense
              height="42"
              hide-details="auto"
              placeholder="Outros"
              :rules="selected ? [$validation.required()] : []"
            />
          </div>
        </template>
      </previous-treatment-medicine>
    </div>
  </expandable-box-node>
</template>

<script>
import { WatchOptionsWithHandler } from 'vue'
import { PropsDefinition, ModelValueProp, Computed } from '@/_typedefs'
import PreviousTreatmentMedicine, { PreviousTreatmentMedicineValue } from './partial/PreviousTreatmentMedicine.vue'
import { cloneDeep, isEmpty } from 'lodash'
import ExpandableBoxNode from '@/components/NewAttendanceForm/FormNodes/ExpandableBox/ExpandableBoxNode.vue'

/** @typedef {Record<string, PreviousTreatmentMedicineValue>} ModelValue */

/**
 * @typedef {Object} PreviousTreatmentNodeProps
 * @property {string} title
 * @property {Record<string, string>} options
 * @property {Record<string, {dosageUnit?: string}>} treatmentsOptions
 * @property {boolean} [hasOther]
 */

/** @typedef {PreviousTreatmentNodeProps & ModelValueProp<ModelValue>} Props */

export default {
  components: { ExpandableBoxNode, PreviousTreatmentMedicine },
  name: 'PreviousTreatmentComponent',
  /** @type {PropsDefinition<Props>} */
  props: {
    value: {
      type: Object,
      default: null,
    },
    title: {
      type: String,
      required: true,
    },
    options: {
      type: Object,
      required: true,
    },
    treatmentsOptions: {
      type: Object,
      default: () => ({}),
    },
    hasOther: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    /** @type {ModelValue} */
    const baseValue = [...Object.values(this.options), ...(this.hasOther ? ['other'] : [])].reduce(
      (acc, curr) => ({ ...acc, [curr]: {} }),
      {},
    )
    /** @type {ModelValue} */
    const previousValue = isEmpty(this.value) ? {} : cloneDeep(this.value)

    /** @type {ModelValue} */
    const modelValue = {
      ...baseValue,
      ...previousValue,
    }

    return {
      opened: false,
      modelValue,
    }
  },
  computed: {
    /** @type {Computed<number>} */
    selectedQuantity() {
      return Object.values(this.modelValue).filter((value) => !!value).length
    },
  },
  methods: {
    toggle() {
      this.opened = !this.opened
    },
  },
  watch: {
    /** @type {WatchOptionsWithHandler<ModelValue>} */
    modelValue: {
      handler(val) {
        const filteredModelValue = Object.fromEntries(Object.entries(val).filter(([_, value]) => !isEmpty(value)))
        if (isEmpty(filteredModelValue)) {
          this.$emit('input', null)
          return
        }

        this.$emit('input', filteredModelValue)
      },
      deep: true,
      immediate: true,
    },
  },
}
</script>

<style scoped>
.border-gray {
  border: 1px solid #e6e6e6;
}

.border-blue {
  border: 1px solid #1976d2;
  box-shadow: inset 0 0 0 1px #1976d2;
}

.rotate-180 {
  transform: rotate(180deg);
}
.expansion-icon {
  transition-property: transform;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 200ms;
}
.divide-y > * + * {
  border-top: 1px solid #e6e6e6;
}

:deep(.v-simple-checkbox .v-icon) {
  font-size: 20px;
}

:deep(label.v-label) {
  color: var(--v-neutralLighten17-base) !important;
}
</style>
